import React from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";

function slideDirection(fromVisual, emptyVisual, gridSize) {
    const sameRow = Math.floor(fromVisual / gridSize) === Math.floor(emptyVisual / gridSize);
    if (fromVisual === emptyVisual - 1 && sameRow) return "right";
    if (fromVisual === emptyVisual + 1 && sameRow) return "left";
    if (fromVisual === emptyVisual - gridSize) return "down";
    if (fromVisual === emptyVisual + gridSize) return "up";
    return null;
}

function DirectionArrow({ direction }) {
    const rotate = { up: 0, right: 90, down: 180, left: 270 }[direction] ?? 0;
    const edge = "8%";
    const pos =
        direction === "right"
            ? { right: edge, top: "50%", transform: `translateY(-50%) rotate(${rotate}deg)` }
            : direction === "left"
              ? { left: edge, top: "50%", transform: `translateY(-50%) rotate(${rotate}deg)` }
              : direction === "down"
                ? { bottom: edge, left: "50%", transform: `translateX(-50%) rotate(${rotate}deg)` }
                : { top: edge, left: "50%", transform: `translateX(-50%) rotate(${rotate}deg)` };
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                width: 0,
                height: 0,
                borderLeft: "0.55em solid transparent",
                borderRight: "0.55em solid transparent",
                borderBottom: "0.9em solid #7dcea0",
                filter: "drop-shadow(0 0 4px rgba(125,206,160,0.85))",
                ...pos,
            }}
        />
    );
}

DirectionArrow.propTypes = {
    direction: PropTypes.oneOf(["up", "down", "left", "right"]).isRequired,
};

/**
 * Light number-mode board: cream tiles + dark numerals (same style for every number).
 * Green arrows on pieces that can slide into the hole.
 */
export default function ScreenshotNumberBoard({ gridSize, startingLayout }) {
    const gs = Number(gridSize) > 1 ? Number(gridSize) : 4;
    const n = gs * gs;
    const cells =
        Array.isArray(startingLayout) && startingLayout.length === n
            ? startingLayout.map((value) => Number(value))
            : Array.from({ length: n }, (_, index) => index);
    const emptySlice = n - 1;
    const emptyVisual = cells.findIndex((value) => value === emptySlice);

    return (
        <Box
            sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                boxSizing: "border-box",
                display: "grid",
                gridTemplateColumns: `repeat(${gs}, minmax(0, 1fr))`,
                gap: "2.2%",
                p: "2.2%",
                bgcolor: "#e8ece8",
                borderRadius: "16px",
            }}
        >
            {cells.map((slice, visual) => {
                const empty = slice === emptySlice;
                const direction = !empty && emptyVisual >= 0 ? slideDirection(visual, emptyVisual, gs) : null;
                return (
                    <Box
                        key={`number-cell-${visual}`}
                        sx={{
                            position: "relative",
                            minWidth: 0,
                            minHeight: 0,
                            containerType: "size",
                            borderRadius: "18%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: empty
                                ? "#d4dad4"
                                : "linear-gradient(145deg, #ffffff 0%, #f4f0e6 48%, #e4dcc8 100%)",
                            color: "#1b4332",
                            boxShadow: empty
                                ? "inset 0 2px 6px rgba(15, 23, 42, 0.12)"
                                : "0 3px 6px rgba(15, 23, 42, 0.12)",
                            ...(direction
                                ? {
                                      outline: "2.5px solid #7dcea0",
                                      outlineOffset: 0,
                                      boxShadow: "0 0 10px rgba(125, 206, 160, 0.85)",
                                  }
                                : {}),
                        }}
                    >
                        {empty ? null : (
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 800,
                                    letterSpacing: "-0.03em",
                                    lineHeight: 1,
                                    color: "inherit",
                                    fontSize: gs >= 6 ? "42cqi" : "48cqi",
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {slice + 1}
                            </Box>
                        )}
                        {direction ? <DirectionArrow direction={direction} /> : null}
                    </Box>
                );
            })}
        </Box>
    );
}

ScreenshotNumberBoard.propTypes = {
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
};
